import { formatAmount, formatShareBps, USD } from "@loot-os/api-contract";
import {
  ECONOMICS_HINTS,
  FEE_COLOR,
  MARGIN_COLOR,
  marginBarShare,
  marginScaleBps,
  purchaseStatusRows,
  type ShopifyEconomicsView,
  VOLUME_SPLIT_HINTS,
  type VolumeSplitKey,
  volumeSplit,
} from "@loot-os/api-contract/shopifyOverview";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { formatNumber } from "@loot-os/ui/lib/format";
import { cn } from "@loot-os/ui/lib/utils";
import { Link, useParams } from "@tanstack/react-router";
import { CircleAlertIcon, CircleCheckIcon, ExternalLinkIcon } from "lucide-react";
import type { ReactNode } from "react";
import { formatDate } from "../lib/format";
import type { ShopifySalesTab } from "../lib/shopifySalesTabs";
import { shopifyAdminUrl } from "../lib/shopifyStore";
import { useShopifyShop } from "../lib/useShopifyShop";

/** Every money figure here is USD, which every linked shop sells in, not the display currency. */
const usd = (cents: number) => formatAmount(cents, USD);

/**
 * Shopify's index, top to bottom: the shop's lifetime economics under the store's name and link
 * state (volume, projected and realized margin, the Loot Labs fee, as `getShopifyEconomics`
 * defines them) with where the volume goes and the two margins side by side;
 * then the purchases by status beside what is waiting on Shopify (orders not yet created there and
 * store credit not yet applied, each linking to its Sales tab).
 */
export function ShopifyOverviewPage() {
  const { status, shop, shopHandle } = useShopifyShop();
  const summary = status.data?.summary;
  if (!shop) return null;

  return (
    <div className="flex flex-col gap-6">
      {summary?.economics && (
        <EconomicsCard
          economics={summary.economics}
          title={
            shopHandle ? (
              <a
                href={shopifyAdminUrl(shopHandle)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 hover:underline"
              >
                {shop.shopDomain}
                <ExternalLinkIcon className="size-3.5 shrink-0 text-muted-foreground" />
              </a>
            ) : (
              shop.shopDomain
            )
          }
          description={`Linked ${shop.linkedAt ? formatDate(shop.linkedAt) : "—"} · ${formatNumber(
            summary.variants,
          )} variants, synced ${shop.lastCatalogSyncAt ? formatDate(shop.lastCatalogSyncAt) : "never"} · All time, USD, refunds excluded`}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <PurchasesCard tally={summary?.purchases} />
        <WaitingCard orders={sum(summary?.dispatches)} storeCredit={sum(summary?.transfers)} />
      </div>
    </div>
  );
}

/** The lifetime figures, then where the volume goes and the margin expected against the margin kept. */
function EconomicsCard({
  economics,
  title,
  description,
}: {
  title: ReactNode;
  description: string;
  economics: ShopifyEconomicsView & {
    volume: { units: number };
    realizedMargin: { settledUnits: number };
    platformFee: { shareBps: number; accruedCents: number; pendingCents: number };
  };
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="min-w-0 truncate">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 xl:grid-cols-4">
          <Figure
            label="Total volume"
            hint={ECONOMICS_HINTS.volume}
            value={usd(economics.volume.cents)}
            note={`${formatNumber(economics.volume.units)} boxes sold`}
          />
          <Figure
            label="Projected margin"
            hint={ECONOMICS_HINTS.projectedMargin}
            value={usd(economics.projectedMargin.cents)}
            percent={economics.projectedMargin.bps}
            note="Your boxes' margin on every box sold"
          />
          <Figure
            label="Realized margin"
            hint={ECONOMICS_HINTS.realizedMargin}
            value={usd(economics.realizedMargin.cents)}
            percent={economics.realizedMargin.bps}
            note={`On ${formatNumber(economics.realizedMargin.settledUnits)} settled boxes`}
          />
          <Figure
            label="Loot Labs volume fee"
            hint={ECONOMICS_HINTS.billedFee}
            value={usd(economics.platformFee.accruedCents)}
            note={
              economics.platformFee.pendingCents > 0
                ? `${formatShareBps(economics.platformFee.shareBps)} of settled boxes, ${usd(economics.platformFee.pendingCents)} not yet billed`
                : `${formatShareBps(economics.platformFee.shareBps)} of settled boxes, billed by Shopify`
            }
          />
        </div>
        <div className="grid gap-x-10 gap-y-6 border-t pt-6 lg:grid-cols-2">
          <VolumeSplitChart economics={economics} />
          <MarginChart economics={economics} />
        </div>
      </CardContent>
    </Card>
  );
}

/** One headline figure with its percent beside it and a line beneath. */
function Figure({
  label,
  hint,
  value,
  percent,
  note,
}: {
  label: string;
  hint: string;
  value: string;
  percent?: number | null;
  note: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Term hint={hint} className="text-xs text-muted-foreground">
        {label}
      </Term>
      <span className="flex flex-wrap items-baseline gap-x-2 text-2xl font-semibold tabular-nums">
        {value}
        {percent !== undefined && (
          <span className="text-sm font-normal text-muted-foreground">
            {percent === null ? "—" : formatShareBps(percent)}
          </span>
        )}
      </span>
      <span className="text-xs text-muted-foreground">{note}</span>
    </div>
  );
}

const SEGMENT_CLASS: Record<VolumeSplitKey, string> = {
  payout: "bg-muted-foreground/25",
  fee: "",
  margin: "",
};
const SEGMENT_COLOR: Partial<Record<VolumeSplitKey, string>> = {
  fee: FEE_COLOR,
  margin: MARGIN_COLOR,
};

/** Every dollar of volume at the projected margin: product paid out, the fee, the margin kept. */
function VolumeSplitChart({ economics }: { economics: ShopifyEconomicsView }) {
  const { segments, netMarginCents } = volumeSplit(economics);
  const drawn = segments.filter((s) => s.share > 0);
  return (
    <figure className="flex flex-col gap-3">
      <figcaption className="text-sm font-medium">Where the volume goes, projected</figcaption>
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-muted">
        {drawn.map((s) => (
          <div
            key={s.key}
            className={cn("h-full first:rounded-l-full last:rounded-r-full", SEGMENT_CLASS[s.key])}
            style={{ width: `${s.share * 100}%`, backgroundColor: SEGMENT_COLOR[s.key] }}
            title={`${s.label}: ${usd(s.cents)} (${percent(s.share)})`}
          />
        ))}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1.5 text-sm">
        {segments.map((s) => (
          <div key={s.key} className="contents">
            <span
              aria-hidden
              className={cn("size-2.5 rounded-sm", SEGMENT_CLASS[s.key])}
              style={{ backgroundColor: SEGMENT_COLOR[s.key] }}
            />
            <dt>
              <Term hint={VOLUME_SPLIT_HINTS[s.key]} className="text-muted-foreground">
                {s.label}
              </Term>
            </dt>
            <dd className="text-right tabular-nums">
              {usd(s.key === "margin" ? netMarginCents : s.cents)}
            </dd>
            <dd className="w-12 text-right text-xs text-muted-foreground tabular-nums">
              {economics.volume.cents > 0 ? percent(s.share) : "—"}
            </dd>
          </div>
        ))}
      </dl>
    </figure>
  );
}

/** The two margins as percents on one scale, the projection lighter than what was kept. */
function MarginChart({ economics }: { economics: ShopifyEconomicsView }) {
  const scale = marginScaleBps(economics);
  const rows = [
    {
      label: "Projected",
      hint: ECONOMICS_HINTS.projectedPercent,
      bps: economics.projectedMargin.bps,
      opacity: 0.45,
    },
    {
      label: "Realized",
      hint: ECONOMICS_HINTS.realizedPercent,
      bps: economics.realizedMargin.bps,
      opacity: 1,
    },
  ];
  return (
    <figure className="flex flex-col gap-3">
      <figcaption className="text-sm font-medium">Margin, projected against realized</figcaption>
      <div className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid grid-cols-[4.5rem_minmax(0,1fr)_3.5rem] items-center gap-3 text-sm"
          >
            <Term hint={row.hint} className="text-muted-foreground">
              {row.label}
            </Term>
            <div className="h-3 rounded-full bg-muted">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${marginBarShare(row.bps, scale) * 100}%`,
                  backgroundColor: MARGIN_COLOR,
                  opacity: row.opacity,
                }}
              />
            </div>
            <span className="text-right tabular-nums">
              {row.bps === null ? "—" : formatShareBps(row.bps)}
            </span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_3.5rem] gap-3 text-xs text-muted-foreground">
        <span />
        <span className="flex justify-between tabular-nums">
          <span>0%</span>
          <span>{formatShareBps(scale)}</span>
        </span>
      </div>
    </figure>
  );
}

/** Every purchase status in the order a box moves through them, each a bar against the largest. */
function PurchasesCard({ tally }: { tally: Record<string, number> | undefined }) {
  const groups = purchaseStatusRows(tally);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Purchases</CardTitle>
        <CardDescription>Every box bought, by where it stands.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {groups.map((group) => (
          <div key={group.label} className="flex flex-col gap-1.5">
            <Term hint={group.hint} className="text-xs font-medium text-muted-foreground">
              {group.label}
            </Term>
            {group.rows.map((row) => (
              <div
                key={row.status}
                className="grid grid-cols-[6rem_minmax(0,1fr)_3rem] items-center gap-3 text-sm"
              >
                <Term hint={row.hint}>{row.label}</Term>
                <div className="h-2 rounded-full bg-muted">
                  {row.count > 0 && (
                    <div
                      className="h-full min-w-1 rounded-full bg-foreground/60"
                      style={{ width: `${row.share * 100}%` }}
                    />
                  )}
                </div>
                <span className="text-right tabular-nums">{formatNumber(row.count)}</span>
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** Rows waiting on Shopify, each linking to the Sales tab that lists and retries them. */
function WaitingCard({ orders, storeCredit }: { orders: number; storeCredit: number }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Waiting on Shopify</CardTitle>
        <CardDescription>Retried on their own; open one to retry it now.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <WaitingRow
          tab="orders"
          label="Orders"
          count={orders}
          note="Claims whose Shopify order is not created yet."
        />
        <WaitingRow
          tab="store-credit"
          label="Store credit"
          count={storeCredit}
          note="Transfers not yet applied to a customer's store credit."
        />
      </CardContent>
    </Card>
  );
}

function WaitingRow({
  tab,
  label,
  count,
  note,
}: {
  tab: ShopifySalesTab;
  label: string;
  count: number;
  note: string;
}) {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const Icon = count > 0 ? CircleAlertIcon : CircleCheckIcon;
  return (
    <Link
      to="/tenants/$tenantId/shopify/sales"
      params={{ tenantId }}
      search={{ tab }}
      className="flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors hover:bg-muted/50"
    >
      <Icon
        className={cn("size-4 shrink-0", count > 0 ? "text-destructive" : "text-muted-foreground")}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{note}</span>
      </div>
      <span
        className={cn(
          "text-lg font-semibold tabular-nums",
          count > 0 ? "text-destructive" : undefined,
        )}
      >
        {formatNumber(count)}
      </span>
    </Link>
  );
}

/** A label with a dotted underline that explains itself on hover. */
function Term({
  hint,
  className,
  children,
}: {
  hint: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              "w-fit cursor-help underline decoration-muted-foreground/50 decoration-dotted underline-offset-4",
              className,
            )}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

function sum(tally: Record<string, number> | undefined): number {
  return Object.values(tally ?? {}).reduce((a, b) => a + b, 0);
}

function percent(share: number): string {
  return `${Math.round(share * 100)}%`;
}
